import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';

// Cómo va hoy.
//
// Todo lo demás del programa cuenta lo que YA PASÓ: el Panel mira treinta días,
// la relación mira una quincena cerrada. Ninguna pantalla contestaba «¿cómo va
// hoy?», que es lo que Gabriel tiene en la cabeza mientras está en la planta con
// el teléfono en la mano.
//
// ── LO QUE LA HACE VALER ──
//
// No es repetir lo que él ya ve. Los 641 kilos los tiene delante; lo que no ve
// es que esa ruta viene en 6,27 y hoy dio 6,44.
//
// Y una cosa llega A TIEMPO: la ruta que todavía no ha reportado. A las tres de
// la tarde eso se arregla con una llamada. Mañana ya es un hueco en los libros
// que nadie sabe explicar — es lo que pasó el 18 de agosto y se descubrió en
// septiembre, con los lotes ya vendidos.
//
// ── EL ORDEN NO ES CAPRICHO ──
//
// Arriba lo que se puede arreglar hoy; abajo lo que solo se puede mirar. Por eso
// «lo que dejó el día» va de último y en pequeño: tiene ruido —los insumos no se
// cargan parejo, un día grande compensa uno chico— y puesto arriba, una cifra
// que salta sin explicación se deja de mirar a la semana.

const diaAnterior = (f) => {
  const d = new Date(String(f).slice(0, 10) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const diaSiguiente = (f) => {
  const d = new Date(String(f).slice(0, 10) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// Un bloque de la pantalla. Todos se leen igual: rótulo chico, cifra grande,
// y debajo el detalle en letra menuda.
function Bloque({ ico, rotulo, cifra, sub, children, pinta }) {
  return (
    <div className="bloque">
      <div className="cuerpo">
        <div style={{ fontSize: 12, color: 'var(--tinta-tenue)',
                      textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {ico} {rotulo}
        </div>
        <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.15,
                      margin: '2px 0', color: pinta || 'inherit' }}>
          {cifra}
        </div>
        {sub && (
          <div style={{ fontSize: 14, color: 'var(--tinta-tenue)' }}>{sub}</div>
        )}
        {children}
      </div>
    </div>
  );
}

function Hoy() {
  const [d, setD] = useState(null);
  const [dia, setDia] = useState(hoy());
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    axios.get(`/api/dia?fecha=${dia}`)
      .then(r => { setD(r.data || null); setError(''); })
      .catch(() => setError('No se pudo cargar. Recargue la página.'));
  }, [dia]);

  useEffect(cargar, [cargar]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!d) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const hayAlgo = d.leche.litros > 0 || d.produccion.kilos > 0
               || d.ventas.plata > 0 || d.cobrado.plata > 0;

  return (
    <>
      <div className="encabezado">
        <h1>{d.es_hoy ? 'Hoy' : fecha(d.fecha)}</h1>
        <p>{d.es_hoy ? fecha(d.fecha) : 'Así fue ese día'}</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button className="boton suave" onClick={() => setDia(diaAnterior(dia))}>
          ◀ El día anterior
        </button>
        {dia < hoy() && (
          <>
            <button className="boton suave" onClick={() => setDia(diaSiguiente(dia))}>
              El siguiente ▶
            </button>
            <button className="boton suave" onClick={() => setDia(hoy())}>
              Volver a hoy
            </button>
          </>
        )}
      </div>

      {/* ── LO QUE TODAVÍA SE PUEDE ARREGLAR ─────────────────────────────
          Va antes que nada, y en rojo. Es lo único de esta pantalla que
          sirve para actuar en vez de para mirar. */}
      {d.leche.faltan.length > 0 && (
        <div className="bloque">
          <div className="cuerpo">
            <div className="aviso malo" style={{ margin: 0 }}>
              <strong>
                {d.leche.faltan.length === 1
                  ? 'Una ruta todavía no reporta'
                  : `${d.leche.faltan.length} rutas todavía no reportan`}
              </strong>
              {d.leche.faltan.map((r, n) => (
                <div key={n} style={{ marginTop: 4 }}>
                  <strong>{r.nombre}</strong> — suele traer{' '}
                  {peso(r.suele_traer, 'L')} al día.
                </div>
              ))}
              <div style={{ marginTop: 6, fontSize: 13 }}>
                {d.es_hoy
                  ? 'Todavía está a tiempo de llamar. Mañana esto es un hueco en los libros.'
                  : 'Ese día no entró leche de esa ruta, o quedó sin anotar.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {!hayAlgo && (
        <div className="bloque">
          <div className="cuerpo">
            <div className="vacio">
              {d.es_hoy
                ? 'Todavía no se ha anotado nada hoy.'
                : 'Ese día no se anotó nada.'}
            </div>
          </div>
        </div>
      )}

      {d.leche.litros > 0 && (
        <Bloque ico="🥛" rotulo="Entró leche"
                cifra={peso(d.leche.litros, 'L')}
                sub={`de ${d.leche.rutas.length} ${d.leche.rutas.length === 1 ? 'ruta' : 'rutas'} · ${plata(d.leche.plata)}`}>
          <div style={{ marginTop: 8 }}>
            {d.leche.rutas.map((r, n) => (
              <div key={n} style={{ display: 'flex', gap: 10, fontSize: 14,
                                    padding: '3px 0' }}>
                <span style={{ flex: 1 }}>{r.ruta}</span>
                <span style={{ fontWeight: 600 }}>{peso(r.litros, 'L')}</span>
                <span style={{ color: 'var(--tinta-tenue)', width: 70,
                               textAlign: 'right' }}>
                  {r.fincas} {r.fincas === 1 ? 'finca' : 'fincas'}
                </span>
              </div>
            ))}
          </div>
        </Bloque>
      )}

      {d.produccion.kilos > 0 && (
        <Bloque ico="🧀" rotulo="Se cuajó"
                cifra={peso(d.produccion.kilos, 'kg')}
                sub={`en ${d.produccion.cuajadas} ${d.produccion.cuajadas === 1 ? 'cuajada' : 'cuajadas'}`}>
          <div style={{ marginTop: 8 }}>
            {d.produccion.por_ruta.map((p, n) => (
              <div key={n} style={{ padding: '6px 0',
                                    borderTop: n ? '1px solid var(--raya)' : 'none' }}>
                <div style={{ display: 'flex', gap: 10, fontSize: 14,
                              alignItems: 'baseline' }}>
                  <span style={{ flex: 1 }}>{p.ruta}</span>
                  <span style={{ fontWeight: 600 }}>{peso(p.kilos, 'kg')}</span>
                  {p.rendimiento && (
                    <span className={'pinta ' + (p.raro ? 'malo' : 'bueno')}>
                      {p.rendimiento} L/kg
                    </span>
                  )}
                </div>
                {/* LO QUE ÉL NO PUEDE VER.
                    Los kilos los tiene delante; que esa ruta viene en 6,27
                    y hoy dio 6,44 es la cuenta que tendría que hacer de
                    memoria. Solo se dice cuando se sale de lo suyo: un
                    aviso que salta todos los días es un adorno. */}
                {p.raro && p.normal && (
                  <div style={{ fontSize: 13, color: 'var(--malo)', marginTop: 2 }}>
                    {p.ruta} viene en {p.normal} L/kg.{' '}
                    {p.peor
                      ? 'Hoy se fue más leche por kilo de queso.'
                      : 'Hoy rindió mejor de lo normal — vale la pena comprobar los litros.'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Bloque>
      )}

      {d.ventas.plata > 0 && (
        <Bloque ico="🛒" rotulo="Salió queso"
                cifra={plata(d.ventas.plata)}
                sub={`${peso(d.ventas.kilos, 'kg')} en ${d.ventas.despachos} ${d.ventas.despachos === 1 ? 'despacho' : 'despachos'}`}>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            Fiado {plata(d.ventas.fiado)} · de contado {plata(d.ventas.contado)}
          </div>
        </Bloque>
      )}

      {d.cobrado.plata > 0 && (
        <Bloque ico="💵" rotulo="Entró plata"
                cifra={plata(d.cobrado.plata)}
                pinta="var(--bueno)"
                sub={`en ${d.cobrado.cuantos} ${d.cobrado.cuantos === 1 ? 'abono' : 'abonos'}`} />
      )}

      {/* ── LO QUE DEJÓ EL DÍA ───────────────────────────────────────────
          De último y en pequeño, a propósito. Tiene ruido: los insumos no se
          cargan parejo y un día grande compensa uno chico. Arriba, una cifra
          que salta sin explicación se deja de mirar a la semana. */}
      {d.produccion.kilos > 0 && (
        <div className="bloque">
          <h2>Lo que dejó el día</h2>
          <div className="cuerpo">
            {d.dejo.sin_precio ? (
              <div className="vacio">
                No hay precio con qué valorar este queso. Póngale uno en 🧀
                Quesos o fije el de la temporada.
              </div>
            ) : (
              <>
                <table className="detalle">
                  <tbody>
                    <tr>
                      <td>Vale el queso que hizo</td>
                      <td className="der">{plata(d.dejo.vale)}</td>
                    </tr>
                    <tr>
                      <td>Menos la leche</td>
                      <td className="der">−{plata(d.dejo.costo_leche)}</td>
                    </tr>
                    {d.dejo.otros_costos > 0 && (
                      <tr>
                        <td>Menos insumos y mano de obra</td>
                        <td className="der">−{plata(d.dejo.otros_costos)}</td>
                      </tr>
                    )}
                    <tr className="neto">
                      <td>{d.dejo.gano >= 0 ? 'GANÓ' : 'PERDIÓ'}</td>
                      <td className="der">{plata(Math.abs(d.dejo.gano))}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="chico" style={{ marginTop: 8 }}>
                  Es del queso de este día, valorado al precio de hoy. No cuenta
                  los gastos de la quesera ni las rutas — para eso está 🛣️ Rutas.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Hoy;
